<template>
  <div class="providers-page">
    <div class="row">
      <div class="col-12 col-md-8 flexben-page-title">
        <h1>{{ $t("flexben.provider.title") }}</h1>
      </div>
    </div>

    <div class="container-bg-white mt-3 providers-page__surface">
      <div class="row p-3 p-md-4">
        <div v-if="!loading && providers.length === 0" class="col-12">
          <div class="providers-empty">
            {{ $t('flexben.provider.card.no_providers') }}
          </div>
        </div>

        <div
          v-for="provider in providers"
          :key="provider.id"
          class="col-12 col-lg-6 col-xxl-4 mb-3 mb-lg-4"
        >
          <article
            class="provider-card h-100"
            :class="{
              'provider-card--contact': provider.can_edit_contact,
              'provider-card--discount-on': provider.discount_module,
            }"
          >
            <header class="provider-card__header">
              <div class="provider-card__identity">
                <div class="provider-card__avatar" aria-hidden="true">
                  {{ providerInitials(provider) }}
                </div>
                <div class="provider-card__titles">
                  <h3 class="provider-card__title">{{ provider.label || provider.name }}</h3>
                  <div class="provider-card__meta">
                    <span class="provider-card__code">{{ provider.name }}</span>
                    <span
                      class="provider-card__pill"
                      :class="provider.discount_module ? 'provider-card__pill--on' : 'provider-card__pill--off'"
                    >
                      {{ provider.discount_module
                        ? $t('flexben.provider.card.discount_on')
                        : $t('flexben.provider.card.discount_off') }}
                    </span>
                  </div>
                </div>
              </div>
            </header>

            <section class="provider-card__discount-panel">
              <div class="provider-card__discount-copy">
                <div class="provider-card__discount-label">
                  {{ $t('flexben.provider.card.discount_module') }}
                </div>
                <div class="provider-card__discount-hint">
                  {{ provider.discount_module
                    ? $t('flexben.provider.card.discount_on')
                    : $t('flexben.provider.card.discount_off') }}
                </div>
              </div>
              <div class="custom-control custom-switch provider-card__switch mb-0">
                <input
                  :id="'discount_module_' + provider.id"
                  type="checkbox"
                  class="custom-control-input"
                  :checked="provider.discount_module"
                  :disabled="discountSubmittingId === provider.id"
                  @change="toggleDiscountModule(provider, $event)"
                />
                <label class="custom-control-label" :for="'discount_module_' + provider.id"></label>
              </div>
            </section>

            <section v-if="provider.can_edit_contact" class="provider-card__contact">
              <div class="provider-card__contact-head">
                <div>
                  <div class="provider-card__section-title">{{ $t('flexben.provider.card.contact') }}</div>
                  <div class="provider-card__section-hint">{{ $t('flexben.provider.card.shown_in_app') }}</div>
                </div>
              </div>

              <div v-if="!isEditingContact(provider)" class="provider-card__contact-actions">
                <button
                  type="button"
                  class="btn provider-card__edit-btn"
                  @click="openContactAccordion(provider)"
                >
                  <font-awesome-icon
                    :icon="hasAnyContact(provider.contact) ? 'pen' : 'plus'"
                    class="mr-2"
                  />
                  {{ hasAnyContact(provider.contact)
                    ? $t('flexben.provider.card.edit_contact')
                    : $t('flexben.provider.card.create_contact') }}
                </button>
              </div>

              <div v-else class="provider-card__contact-form">
                <div v-if="contactFormError" class="alert alert-danger py-2 mb-3">
                  {{ contactFormError }}
                </div>

                <div class="form-group">
                  <label>{{ $t('flexben.provider.contact_modal.phone') }}</label>
                  <input
                    v-model.trim="contactForm.phone"
                    type="text"
                    class="form-control"
                    :class="{'border-danger': Boolean(contactErrors.phone)}"
                    maxlength="100"
                    @input="clearContactFieldError('phone')"
                  />
                  <small v-if="contactErrors.phone" class="text-danger d-block mt-1">{{ contactErrors.phone }}</small>
                </div>
                <div class="form-group">
                  <label>{{ $t('flexben.provider.contact_modal.email') }}</label>
                  <input
                    v-model.trim="contactForm.email"
                    type="email"
                    class="form-control"
                    :class="{'border-danger': Boolean(contactErrors.email)}"
                    maxlength="255"
                    @input="clearContactFieldError('email')"
                  />
                  <small v-if="contactErrors.email" class="text-danger d-block mt-1">{{ contactErrors.email }}</small>
                </div>
                <div class="form-group">
                  <label>{{ $t('flexben.provider.contact_modal.site') }}</label>
                  <input
                    v-model.trim="contactForm.site"
                    type="text"
                    class="form-control"
                    :class="{'border-danger': Boolean(contactErrors.site)}"
                    maxlength="500"
                    @input="clearContactFieldError('site')"
                  />
                  <small v-if="contactErrors.site" class="text-danger d-block mt-1">{{ contactErrors.site }}</small>
                  <small v-else-if="contactForm.providerName === 'vd'" class="form-text text-muted">
                    {{ $t('flexben.provider.contact_modal.site_hint_vd') }}
                  </small>
                </div>
                <div class="form-group">
                  <label>{{ $t('flexben.provider.contact_modal.address') }}</label>
                  <input
                    v-model.trim="contactForm.address"
                    type="text"
                    class="form-control"
                    :class="{'border-danger': Boolean(contactErrors.address)}"
                    maxlength="500"
                    @input="clearContactFieldError('address')"
                  />
                  <small v-if="contactErrors.address" class="text-danger d-block mt-1">{{ contactErrors.address }}</small>
                </div>
                <div class="form-row">
                  <div class="form-group col-6">
                    <label>{{ $t('flexben.provider.contact_modal.lat') }}</label>
                    <input
                      v-model.trim="contactForm.lat"
                      type="text"
                      inputmode="decimal"
                      class="form-control"
                      :class="{'border-danger': Boolean(contactErrors.lat)}"
                      @input="clearContactFieldError('lat')"
                    />
                    <small v-if="contactErrors.lat" class="text-danger d-block mt-1">{{ contactErrors.lat }}</small>
                  </div>
                  <div class="form-group col-6">
                    <label>{{ $t('flexben.provider.contact_modal.lng') }}</label>
                    <input
                      v-model.trim="contactForm.lng"
                      type="text"
                      inputmode="decimal"
                      class="form-control"
                      :class="{'border-danger': Boolean(contactErrors.lng)}"
                      @input="clearContactFieldError('lng')"
                    />
                    <small v-if="contactErrors.lng" class="text-danger d-block mt-1">{{ contactErrors.lng }}</small>
                  </div>
                </div>

                <div class="provider-card__form-actions">
                  <button
                    type="button"
                    class="btn provider-card__cancel-btn"
                    :disabled="contactSubmitting"
                    @click="closeContactAccordion"
                  >
                    {{ $t('flexben.provider.contact_modal.cancel') }}
                  </button>
                  <button
                    type="button"
                    class="btn save-btn"
                    :disabled="contactSubmitting"
                    @click="saveContact"
                  >
                    {{ $t('flexben.provider.contact_modal.save') }}
                  </button>
                </div>
              </div>
            </section>
          </article>
        </div>
      </div>
    </div>

    <content-loader :loader-visible="loading" :loader-error="loaderError"></content-loader>
  </div>
</template>

<script>
import httpFlexben from "@/http/http-flexben";

export default {
  name: 'FlexBenProviders',
  data() {
    return {
      loading: false,
      loaderError: false,
      providers: [],
      discountSubmittingId: null,
      editingContactId: null,
      contactSubmitting: false,
      contactFormError: '',
      contactErrors: {},
      contactForm: {
        id: null,
        providerName: '',
        site: '',
        email: '',
        phone: '',
        address: '',
        lat: '',
        lng: '',
      },
    }
  },
  methods: {
    providerInitials(provider) {
      const label = (provider.label || provider.name || '').trim();

      if (!label) {
        return 'P';
      }

      const parts = label.split(/\s+/).filter(Boolean);

      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
      }

      return (parts[0][0] + parts[1][0]).toUpperCase();
    },

    hasAnyContact(contact) {
      if (!contact) {
        return false;
      }

      return ['phone', 'email', 'site', 'address', 'lat', 'lng'].some((key) => {
        return contact[key] !== null && contact[key] !== undefined && String(contact[key]).trim() !== '';
      });
    },

    isEditingContact(provider) {
      return String(this.editingContactId) === String(provider.id);
    },

    notify(message, variant = 'info', title = null) {
      if (!this.$bvToast) {
        return;
      }

      this.$bvToast.toast(message, {
        title: title || this.$t('flexben.provider.title'),
        autoHideDelay: 5000,
        toaster: 'b-toaster-bottom-center',
        variant: variant,
      });
    },

    getApiErrorMessage(error, fallbackKey) {
      const data = error && error.response ? error.response.data : null;

      if (data) {
        if (data.errors && typeof data.errors === 'object') {
          const fieldMessages = Object.keys(data.errors).reduce((messages, field) => {
            const value = data.errors[field];

            if (Array.isArray(value)) {
              return messages.concat(value);
            }

            if (value) {
              messages.push(String(value));
            }

            return messages;
          }, []);

          if (fieldMessages.length > 0) {
            return fieldMessages.join(' ');
          }
        }

        if (data.message) {
          return data.message;
        }

        if (data.error) {
          return data.error;
        }
      }

      return this.$t(fallbackKey || 'flexben.general.error_ups');
    },

    applyApiFieldErrors(error) {
      const data = error && error.response ? error.response.data : null;
      const apiErrors = data && data.errors && typeof data.errors === 'object' ? data.errors : {};
      const nextErrors = {};

      ['phone', 'email', 'site', 'address', 'lat', 'lng'].forEach((field) => {
        if (!apiErrors[field]) {
          return;
        }

        nextErrors[field] = Array.isArray(apiErrors[field])
          ? apiErrors[field][0]
          : String(apiErrors[field]);
      });

      this.contactErrors = nextErrors;
    },

    clearContactFieldError(field) {
      if (!this.contactErrors[field]) {
        return;
      }

      this.$delete(this.contactErrors, field);

      if (Object.keys(this.contactErrors).length === 0) {
        this.contactFormError = '';
      }
    },

    resetContactFormState() {
      this.contactErrors = {};
      this.contactFormError = '';
      this.contactSubmitting = false;
      this.contactForm = {
        id: null,
        providerName: '',
        site: '',
        email: '',
        phone: '',
        address: '',
        lat: '',
        lng: '',
      };
    },

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },

    isValidSite(value) {
      return /^https:\/\//i.test(String(value || '').trim());
    },

    isValidCoordinate(value, min, max) {
      if (value === '' || value === null || value === undefined) {
        return true;
      }

      const normalized = String(value).trim().replace(',', '.');
      if (!/^-?\d+(\.\d+)?$/.test(normalized)) {
        return false;
      }

      const numeric = Number(normalized);
      return Number.isFinite(numeric) && numeric >= min && numeric <= max;
    },

    normalizeCoordinate(value) {
      if (value === '' || value === null || value === undefined) {
        return '';
      }

      return String(value).trim().replace(',', '.');
    },

    validateContactForm() {
      const errors = {};
      const phone = (this.contactForm.phone || '').trim();
      const email = (this.contactForm.email || '').trim();
      const site = (this.contactForm.site || '').trim();
      const address = (this.contactForm.address || '').trim();
      const lat = this.normalizeCoordinate(this.contactForm.lat);
      const lng = this.normalizeCoordinate(this.contactForm.lng);

      if (phone.length > 100) {
        errors.phone = this.$t('flexben.provider.contact_modal.validation.phone_too_long');
      }

      if (email.length > 255) {
        errors.email = this.$t('flexben.provider.contact_modal.validation.email_too_long');
      } else if (email && !this.isValidEmail(email)) {
        errors.email = this.$t('flexben.provider.contact_modal.validation.email_invalid');
      }

      if (site.length > 500) {
        errors.site = this.$t('flexben.provider.contact_modal.validation.site_too_long');
      } else if (site && !this.isValidSite(site)) {
        errors.site = this.$t('flexben.provider.contact_modal.validation.site_invalid');
      }

      if (address.length > 500) {
        errors.address = this.$t('flexben.provider.contact_modal.validation.address_too_long');
      }

      if (!this.isValidCoordinate(lat, -90, 90)) {
        errors.lat = this.$t('flexben.provider.contact_modal.validation.lat_invalid');
      }

      if (!this.isValidCoordinate(lng, -180, 180)) {
        errors.lng = this.$t('flexben.provider.contact_modal.validation.lng_invalid');
      }

      this.contactErrors = errors;
      this.contactFormError = Object.keys(errors).length > 0
        ? this.$t('flexben.provider.contact_modal.validation.form_invalid')
        : '';

      return Object.keys(errors).length === 0;
    },

    loadProviders() {
      this.loading = true;
      this.loaderError = false;

      httpFlexben.provider.all()
        .then((response) => {
          const items = (response.data && response.data.items) ? response.data.items : [];
          this.providers = items.map((item) => ({
            ...item,
            contact: item.contact || { site: '', email: '', phone: '', address: '', lat: '', lng: '' },
            discount_module: Boolean(item.discount_module),
            can_edit_contact: Boolean(item.can_edit_contact),
          }));
        })
        .catch((error) => {
          this.loaderError = true;
          this.providers = [];
          this.notify(
            this.getApiErrorMessage(error, 'flexben.provider.card.load_error'),
            'danger',
            this.$t('flexben.general.error')
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },

    toggleDiscountModule(provider, event) {
      const nextValue = Boolean(event.target.checked);
      const previousValue = Boolean(provider.discount_module);

      this.discountSubmittingId = provider.id;
      provider.discount_module = nextValue;

      httpFlexben.provider.config.discountModule({
        id: provider.id,
        action: nextValue,
      }).then(() => {
        this.notify(this.$t('flexben.provider.card.discount_success'), 'success');
      }).catch((error) => {
        provider.discount_module = previousValue;
        event.target.checked = previousValue;
        this.notify(
          this.getApiErrorMessage(error, 'flexben.provider.card.discount_error'),
          'danger',
          this.$t('flexben.general.error')
        );
      }).finally(() => {
        this.discountSubmittingId = null;
      });
    },

    openContactAccordion(provider) {
      const contact = provider.contact || {};

      this.resetContactFormState();
      this.editingContactId = provider.id;
      this.contactForm = {
        id: provider.id,
        providerName: provider.name || '',
        site: contact.site || '',
        email: contact.email || '',
        phone: contact.phone || '',
        address: contact.address || '',
        lat: contact.lat !== null && contact.lat !== undefined ? String(contact.lat) : '',
        lng: contact.lng !== null && contact.lng !== undefined ? String(contact.lng) : '',
      };
    },

    closeContactAccordion() {
      this.editingContactId = null;
      this.resetContactFormState();
    },

    saveContact() {
      if (!this.contactForm.id || this.contactSubmitting) {
        return;
      }

      if (!this.validateContactForm()) {
        this.notify(
          this.$t('flexben.provider.contact_modal.validation.form_invalid'),
          'warning',
          this.$t('flexben.general.validation_errors')
        );
        return;
      }

      this.contactSubmitting = true;
      this.contactFormError = '';

      httpFlexben.provider.config.contact({
        id: this.contactForm.id,
        site: this.contactForm.site,
        email: this.contactForm.email,
        phone: this.contactForm.phone,
        address: this.contactForm.address,
        lat: this.normalizeCoordinate(this.contactForm.lat),
        lng: this.normalizeCoordinate(this.contactForm.lng),
      }).then((response) => {
        const item = response.data && response.data.item ? response.data.item : null;
        const providerIndex = this.providers.findIndex((provider) => String(provider.id) === String(this.contactForm.id));

        if (providerIndex !== -1) {
          const nextContact = (item && item.contact)
            ? item.contact
            : {
              site: this.contactForm.site,
              email: this.contactForm.email,
              phone: this.contactForm.phone,
              address: this.contactForm.address,
              lat: this.normalizeCoordinate(this.contactForm.lat),
              lng: this.normalizeCoordinate(this.contactForm.lng),
            };

          this.$set(this.providers[providerIndex], 'contact', nextContact);

          if (item && item.config) {
            this.$set(this.providers[providerIndex], 'config', item.config);
          }
        }

        this.closeContactAccordion();
        this.notify(this.$t('flexben.provider.contact_modal.success'), 'success');
      }).catch((error) => {
        this.applyApiFieldErrors(error);
        const message = this.getApiErrorMessage(error, 'flexben.provider.contact_modal.save_error');
        this.contactFormError = message;
        this.notify(message, 'danger', this.$t('flexben.general.error'));
      }).finally(() => {
        this.contactSubmitting = false;
      });
    }
  },
  mounted() {
    this.loadProviders();
  },
}
</script>

<style scoped>
.providers-page__surface {
  border-radius: 12px;
}

.providers-empty {
  padding: 48px 16px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}

.provider-card {
  --provider-accent: #f49400;
  --provider-ink: #1f2937;
  --provider-muted: #6b7280;
  --provider-line: #eceff3;
  --provider-soft: #f8f9fb;

  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--provider-line);
  border-radius: 16px;
  background:
    linear-gradient(180deg, #ffffff 0%, #fcfcfd 100%);
  box-shadow: 0 8px 24px rgba(17, 24, 39, 0.04);
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}

.provider-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--provider-accent), #ffc15a);
}

.provider-card:hover {
  transform: translateY(-2px);
  border-color: #e2e8f0;
  box-shadow: 0 14px 32px rgba(17, 24, 39, 0.08);
}

.provider-card--discount-on {
  border-color: rgba(244, 148, 0, 0.28);
}

.provider-card__header {
  padding: 20px 20px 12px;
}

.provider-card__identity {
  display: flex;
  align-items: center;
  gap: 14px;
}

.provider-card__avatar {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #7a4a00;
  background: linear-gradient(145deg, #ffe7bf 0%, #ffd58a 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.provider-card__titles {
  min-width: 0;
}

.provider-card__title {
  margin: 0;
  font-size: 18px;
  line-height: 1.25;
  font-weight: 700;
  color: var(--provider-ink);
}

.provider-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}

.provider-card__code {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--provider-muted);
}

.provider-card__pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.provider-card__pill--on {
  color: #166534;
  background: #dcfce7;
}

.provider-card__pill--off {
  color: #6b7280;
  background: #f3f4f6;
}

.provider-card__discount-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 20px;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--provider-soft);
  border: 1px solid #eef1f5;
}

.provider-card__discount-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--provider-ink);
}

.provider-card__discount-hint {
  margin-top: 2px;
  font-size: 12px;
  color: var(--provider-muted);
}

.provider-card__switch {
  padding-left: 2.25rem;
  min-height: 1.5rem;
}

.provider-card__contact {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px 20px 20px;
}

.provider-card__contact-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.provider-card__section-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--provider-ink);
}

.provider-card__section-hint {
  margin-top: 2px;
  font-size: 12px;
  color: var(--provider-muted);
}

.provider-card__contact-actions {
  display: flex;
  justify-content: flex-start;
}

.provider-card__contact-form {
  padding: 14px;
  border-radius: 12px;
  border: 1px solid #eef1f5;
  background: #fff;
}

.provider-card__contact-form .form-group {
  margin-bottom: 12px;
}

.provider-card__contact-form .form-group label {
  font-size: 12px;
  font-weight: 600;
  color: var(--provider-muted);
}

.provider-card__form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}

.provider-card__edit-btn {
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #7a4a00;
  background: #fff4e0;
  border: 1px solid #ffdca8;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.provider-card__edit-btn:hover,
.provider-card__edit-btn:focus {
  color: #fff;
  background: var(--provider-accent);
  border-color: var(--provider-accent);
}

.provider-card__cancel-btn {
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: var(--provider-muted);
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
}
</style>
